import { useState } from 'react';
import {
  Box,
  Flex,
  Grid,
  Stack,
  Heading,
  Text,
  Tag,
  Link,
  Wrap,
  WrapItem,
  useColorMode,
} from '@chakra-ui/react';
import PageLayout from '../layouts/PageLayout';
import companyProjects, {
  CompanyProject,
  ProjectChunk,
} from '../../data/companyProjects';
import openSourceProjects from '../../data/projectsShowcase';

const OPEN_SOURCE_ID = 'open-source';

function BackPill({ colorMode }) {
  return (
    <Link href="/" _hover={{ textDecoration: 'none' }} alignSelf="flex-start">
      <Tag
        fontWeight="bold"
        fontSize="xs"
        letterSpacing="wide"
        px={3}
        py={1}
        rounded="sm"
        bg="transparent"
        border="1px solid"
        borderColor={`mode.${colorMode}.accent`}
        color={`mode.${colorMode}.accent`}
      >
        Projects
      </Tag>
    </Link>
  );
}

function NavItem({ label, active, onClick, colorMode }) {
  return (
    <Box
      as="button"
      onClick={onClick}
      textAlign="left"
      w="100%"
      px={4}
      py={4}
      fontSize="md"
      fontWeight={active ? 'bold' : 'medium'}
      color={active ? `mode.${colorMode}.text` : `mode.${colorMode}.subtext`}
      bg={active ? `mode.${colorMode}.accentBG` : 'transparent'}
      borderLeft="3px solid"
      borderLeftColor={active ? `mode.${colorMode}.accent` : 'transparent'}
      _hover={{ color: `mode.${colorMode}.text` }}
    >
      {label}
    </Box>
  );
}

function SectionLabel({ children, colorMode }) {
  return (
    <Text
      fontSize="xs"
      fontWeight="bold"
      letterSpacing="wide"
      textTransform="uppercase"
      color={`mode.${colorMode}.subtext`}
      px={4}
      mb={2}
    >
      {children}
    </Text>
  );
}

function TechTag({ children, colorMode }) {
  return (
    <Tag
      size="sm"
      fontWeight="semibold"
      bg={`mode.${colorMode}.career.tagBG`}
      color={`mode.${colorMode}.career.subtext`}
    >
      {children}
    </Tag>
  );
}

function CompanyPanel({
  company,
  colorMode,
}: {
  company: CompanyProject;
  colorMode: string;
}) {
  return (
    <Stack spacing={0}>
      <Stack spacing={1}>
        <Heading
          fontSize={{ base: 'xl', md: '2xl' }}
          color={`mode.${colorMode}.text`}
        >
          {company.name}
        </Heading>
        <Text fontSize="sm" color={`mode.${colorMode}.subtext`}>
          {company.role} &middot; {company.period}
        </Text>
      </Stack>

      {company.projects.map((project) => (
        <Stack key={project.id} spacing={4} py={6}>
          <Text
            fontSize="xs"
            fontWeight="bold"
            textTransform="uppercase"
            letterSpacing="wide"
            color={`mode.${colorMode}.accent`}
          >
            {project.category}
          </Text>
          <Heading
            as="h3"
            fontSize={{ base: 'xl', md: '2xl' }}
            color={`mode.${colorMode}.text`}
          >
            {project.title}
          </Heading>
          <ProjectDescription project={project} colorMode={colorMode} />
        </Stack>
      ))}
    </Stack>
  );
}

function ProjectDescription({
  project,
  colorMode,
}: {
  project: ProjectChunk;
  colorMode: string;
}) {
  return (
    <Stack spacing={5}>
      <Text color={`mode.${colorMode}.subtext`}>{project.summary}</Text>

      <Stack spacing={3}>
        <Text
          fontSize="xs"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing="wide"
          color={`mode.${colorMode}.text`}
        >
          {project.placeholder ? 'What to add' : 'What I built'}
        </Text>
        <Stack as="ul" spacing={2} listStyleType="none" m={0}>
          {project.contributions.map((contribution) => (
            <Flex as="li" key={contribution} align="flex-start" gap={3}>
              <Box
                mt="0.55em"
                w={1.5}
                h={1.5}
                flexShrink={0}
                rounded="full"
                bg={`mode.${colorMode}.accent`}
              />
              <Text fontSize="sm" color={`mode.${colorMode}.subtext`}>
                {contribution}
              </Text>
            </Flex>
          ))}
        </Stack>
      </Stack>

      <Box
        borderLeft="3px solid"
        borderColor={`mode.${colorMode}.accent`}
        bg={`mode.${colorMode}.accentBG`}
        px={4}
        py={3}
      >
        <Text
          fontSize="xs"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing="wide"
          color={`mode.${colorMode}.accent`}
          mb={1}
        >
          {project.placeholder ? 'Details needed' : 'Outcome'}
        </Text>
        <Text fontSize="sm" color={`mode.${colorMode}.subtext`}>
          {project.outcome}
        </Text>
      </Box>
    </Stack>
  );
}

function OpenSourcePanel({ colorMode }) {
  return (
    <Stack spacing={6}>
      <Stack spacing={1}>
        <Heading
          fontSize={{ base: 'xl', md: '2xl' }}
          color={`mode.${colorMode}.text`}
        >
          Open Source
        </Heading>
        <Text fontSize="sm" color={`mode.${colorMode}.subtext`}>
          Side projects &amp; experiments
        </Text>
      </Stack>

      <Box
        bg={`mode.${colorMode}.cardBG`}
        border="1px solid"
        borderColor={`mode.${colorMode}.border`}
        rounded="md"
        p={{ base: 5, md: 8 }}
      >
        <Stack
          spacing={6}
          divider={
            <Box
              borderTop="1px solid"
              borderColor={`mode.${colorMode}.border`}
            />
          }
        >
          {openSourceProjects.map((project) => (
            <Stack key={project.name} spacing={2}>
              <Link
                href={project.website}
                isExternal
                fontWeight="bold"
                color={`mode.${colorMode}.text`}
                _hover={{ color: `mode.${colorMode}.accent` }}
              >
                {project.name}
              </Link>
              <Text fontSize="sm" color={`mode.${colorMode}.subtext`}>
                {project.description}
              </Text>
              <Wrap spacing={2}>
                {project.techstack.map((tech) => (
                  <WrapItem key={tech}>
                    <TechTag colorMode={colorMode}>{tech}</TechTag>
                  </WrapItem>
                ))}
              </Wrap>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}

function ProjectsPage() {
  const { colorMode } = useColorMode();
  const [selectedId, setSelectedId] = useState<string>(companyProjects[0].id);

  const selectedCompany = companyProjects.find((c) => c.id === selectedId);
  const isOpenSource = selectedId === OPEN_SOURCE_ID;

  return (
    <PageLayout seoKey="projects">
      <Stack
        as="main"
        w="100%"
        maxW="1100px"
        mx="auto"
        px={{ base: 4, md: 8 }}
        py={{ base: 6, md: 10 }}
        spacing={6}
      >
        <BackPill colorMode={colorMode} />

        <Heading
          as="h1"
          color={`mode.${colorMode}.text`}
          fontSize={{ base: '3xl', sm: '4xl', md: '5xl' }}
          fontWeight="bold"
        >
          What I&apos;ve built
        </Heading>

        <Grid
          templateColumns={{
            base: 'minmax(0, 1fr)',
            md: '260px minmax(0, 1fr)',
          }}
          border="1px solid"
          borderColor={`mode.${colorMode}.border`}
          rounded="md"
        >
          <Box
            minW={0}
            borderBottomWidth={{ base: '1px', md: 0 }}
            borderRightWidth={{ base: 0, md: '1px' }}
            borderColor={`mode.${colorMode}.border`}
            py={6}
          >
            <Stack spacing={0}>
              <SectionLabel colorMode={colorMode}>Companies</SectionLabel>
              <Stack direction="column" spacing={0}>
                {companyProjects.map((company) => (
                  <NavItem
                    key={company.id}
                    label={company.name}
                    active={selectedId === company.id}
                    onClick={() => setSelectedId(company.id)}
                    colorMode={colorMode}
                  />
                ))}
              </Stack>
            </Stack>

            <Box
              borderTop="1px solid"
              borderColor={`mode.${colorMode}.border`}
              mt={8}
              pt={6}
            >
              <Stack spacing={0}>
                <SectionLabel colorMode={colorMode}>Open Source</SectionLabel>
                <NavItem
                  label="Open Source"
                  active={isOpenSource}
                  onClick={() => setSelectedId(OPEN_SOURCE_ID)}
                  colorMode={colorMode}
                />
              </Stack>
            </Box>
          </Box>

          <Box minW={0} p={{ base: 5, md: 8 }}>
            {isOpenSource || !selectedCompany ? (
              <OpenSourcePanel colorMode={colorMode} />
            ) : (
              <CompanyPanel company={selectedCompany} colorMode={colorMode} />
            )}
          </Box>
        </Grid>
      </Stack>
    </PageLayout>
  );
}

export default ProjectsPage;
